import React, { useState } from "react";
import { kitchenService } from "../../services";
import { toast } from "react-toastify";

interface Form3Props {
  onNext: (data: Form3Data) => void;
  initialData?: Form3Data;
}

export interface Form3Data {
  address: string;
  landmark: string;
  state: string;
  city: string;
  pincode: string;
  lat: number;
  lng: number;
  googleMapsLink?: string;
}

const Form3: React.FC<Form3Props> = ({ onNext, initialData }) => {
  const [formData, setFormData] = useState<Form3Data>({
    address: initialData?.address || "",
    landmark: initialData?.landmark || "",
    state: initialData?.state || "",
    city: initialData?.city || "",
    pincode: initialData?.pincode || "",
    lat: initialData?.lat || 0,
    lng: initialData?.lng || 0,
    googleMapsLink: initialData?.googleMapsLink || "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Indian states list
  const indianStates = [
    "Andhra Pradesh",
    "Arunachal Pradesh",
    "Assam",
    "Bihar",
    "Chhattisgarh",
    "Goa",
    "Gujarat",
    "Haryana",
    "Himachal Pradesh",
    "Jharkhand",
    "Karnataka",
    "Kerala",
    "Madhya Pradesh",
    "Maharashtra",
    "Manipur",
    "Meghalaya",
    "Mizoram",
    "Nagaland",
    "Odisha",
    "Punjab",
    "Rajasthan",
    "Sikkim",
    "Tamil Nadu",
    "Telangana",
    "Tripura",
    "Uttar Pradesh",
    "Uttarakhand",
    "West Bengal",
    "Andaman and Nicobar Islands",
    "Chandigarh",
    "Dadra and Nagar Haveli and Daman and Diu",
    "Delhi",
    "Jammu and Kashmir",
    "Ladakh",
    "Lakshadweep",
    "Puducherry",
  ];

  // Function to extract lat/lng from Google Maps URL
  const extractCoordinatesFromUrl = async (url: string) => {
    try {
      let resolvedUrl = url;

      // Check if it's a shortened Google Maps URL
      if (url.includes('maps.app.goo.gl') || url.includes('goo.gl')) {
        try {
          // Try to resolve shortened URL using a CORS proxy
          const corsProxy = 'https://api.allorigins.win/raw?url=';
          const response = await fetch(corsProxy + encodeURIComponent(url));
          const text = await response.text();
          
          // Extract the redirect URL from the response
          const urlMatch = text.match(/https:\/\/www\.google\.com\/maps[^\s"<>]*/);
          if (urlMatch) {
            resolvedUrl = urlMatch[0];
          } else {
            // If we can't resolve it, show a helpful message
            toast.info(
              "For shortened URLs, please open the link in Google Maps, then click 'Share' and copy the full URL (uncheck 'Shorten URL' option)",
              { autoClose: 7000 }
            );
            return;
          }
        } catch (resolveError) {
          console.error("Error resolving shortened URL:", resolveError);
          toast.warning(
            "Cannot resolve shortened URL. Please use the full Google Maps URL. Open the link, click 'Share', uncheck 'Shorten URL', and copy the full link.",
            { autoClose: 7000 }
          );
          return;
        }
      }

      // Try different patterns to extract coordinates
      let lat: number | null = null;
      let lng: number | null = null;

      // Pattern 1: @latitude,longitude format (e.g., /@28.5355,77.3910,17z)
      const coordPattern1 = /@(-?\d+\.\d+),(-?\d+\.\d+)/;
      const match1 = resolvedUrl.match(coordPattern1);
      if (match1) {
        lat = parseFloat(match1[1]);
        lng = parseFloat(match1[2]);
      }

      // Pattern 2: ?q=latitude,longitude format
      if (!lat || !lng) {
        const coordPattern2 = /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/;
        const match2 = resolvedUrl.match(coordPattern2);
        if (match2) {
          lat = parseFloat(match2[1]);
          lng = parseFloat(match2[2]);
        }
      }

      // Pattern 3: !3d and !4d format (3d is latitude, 4d is longitude)
      if (!lat || !lng) {
        const latPattern = /!3d(-?\d+\.\d+)/;
        const lngPattern = /!4d(-?\d+\.\d+)/;
        const latMatch = resolvedUrl.match(latPattern);
        const lngMatch = resolvedUrl.match(lngPattern);
        if (latMatch && lngMatch) {
          lat = parseFloat(latMatch[1]);
          lng = parseFloat(lngMatch[1]);
        }
      }

      if (lat !== null && lng !== null) {
        // Store non-null values in local variables for TypeScript type narrowing
        const validLat = lat;
        const validLng = lng;
        setFormData((prev) => ({
          ...prev,
          lat: validLat,
          lng: validLng,
        }));
        toast.success("Coordinates extracted successfully!");
      } else {
        toast.warning(
          "Could not extract coordinates. Please use a full Google Maps URL or enter coordinates manually.",
          { autoClose: 5000 }
        );
      }
    } catch (error) {
      console.error("Error extracting coordinates:", error);
      toast.error(
        "Failed to extract coordinates. Try using the full URL from Google Maps 'Share' option.",
        { autoClose: 5000 }
      );
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    
    // Special handling for Google Maps link
    if (name === "googleMapsLink" && value.trim()) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      
      // Try to extract coordinates from the URL
      if (value.includes('google.com/maps') || value.includes('maps.app.goo.gl') || value.includes('goo.gl/maps')) {
        extractCoordinatesFromUrl(value);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: name === "lat" || name === "lng" ? parseFloat(value) || 0 : value,
      }));
    }
    
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.address.trim()) {
      setError("Address is required");
      return;
    }
    if (!formData.state) {
      setError("Please select a state");
      return;
    }
    if (!formData.city.trim()) {
      setError("City is required");
      return;
    }
    if (!formData.pincode.trim()) {
      setError("Pincode is required");
      return;
    }
    if (!/^[0-9]{6}$/.test(formData.pincode.trim())) {
      setError("Please enter a valid 6-digit pincode");
      return;
    }

    setLoading(true);
    try {
      // Submit to API
      const response: any = await kitchenService.submitStep3(formData);
      toast.success(response.message || "Location details saved successfully!");
      // Move to next step
      onNext(formData);
    } catch (err: any) {
      setError(err.message || "Failed to save data. Please try again.");
      toast.error(err.message || "Failed to save location details");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="">
      <div className="w-full">
        <h2 className="text-[35px] font-Urbanist font-semibold text-gray-900 mb-2 text-left leading-[125%]">
          Location & Address
        </h2>
        <p className="text-[#6A6A6A] text-[15px] font-medium leading-[140%]">
          Enter these details to get started
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-5 mt-[20px]">
          {/* Location of your Kitchen */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="address"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Location of your Kitchen
            </label>
            <div className="relative">
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Add Location"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Landmark */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="landmark"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Landmark
            </label>
            <input
              type="text"
              id="landmark"
              name="landmark"
              value={formData.landmark}
              onChange={handleChange}
              placeholder="Landmark"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
            />
          </div>

          {/* State and City - Side by Side */}
          <div className="grid grid-cols-2 gap-4">
            {/* State */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="state"
                className="text-sm font-Urbanist font-medium text-gray-700"
              >
                State
              </label>
              <select
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all cursor-pointer"
                required
              >
                <option value="" disabled>
                  Select State
                </option>
                {indianStates.map((state) => (
                  <option key={state} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            {/* City */}
            <div className="flex flex-col gap-2">
              <label
                htmlFor="city"
                className="text-sm font-Urbanist font-medium text-gray-700"
              >
                City
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Select City"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
                required
              />
            </div>
          </div>

          {/* Pincode */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="pincode"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Pincode
            </label>
            <input
              type="text"
              id="pincode"
              name="pincode"
              value={formData.pincode}
              onChange={handleChange}
              placeholder="Enter 6 digit Pincode"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              required
              maxLength={6}
            />
          </div>

          {/* Google Maps Link */}
          <div className="flex flex-col gap-2">
            <label
              htmlFor="googleMapsLink"
              className="text-sm font-Urbanist font-medium text-gray-700"
            >
              Google Maps Link
            </label>
            <input
              type="url"
              id="googleMapsLink"
              name="googleMapsLink"
              value={formData.googleMapsLink}
              onChange={handleChange}
              placeholder="Enter link to google maps"
              className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
            />
            {/* <p className="text-xs text-gray-500 mt-1">
              💡 Tip: In Google Maps, click "Share" → uncheck "Shorten URL" → copy the full link for automatic coordinate extraction
            </p> */}
          </div>

          {/* Lat/Long */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <label
                htmlFor="lat"
                className="text-sm font-Urbanist font-medium text-gray-700"
              >
                Latitude
              </label>
              <input
                type="number"
                step="any"
                id="lat"
                name="lat"
                value={formData.lat || ""}
                onChange={handleChange}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="Enter Latitude"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label
                htmlFor="lng"
                className="text-sm font-Urbanist font-medium text-gray-700"
              >
                Longitude
              </label>
              <input
                type="number"
                step="any"
                id="lng"
                name="lng"
                value={formData.lng || ""}
                onChange={handleChange}
                onWheel={(e) => e.currentTarget.blur()}
                placeholder="Enter Longitude"
                className="w-full px-4 py-3 text-sm font-Urbanist border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-orange-500 focus:bg-white transition-all"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm mt-2">
              {error}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mt-2">
            <button
              type="submit"
              className="w-full px-6 py-3.5 text-base font-Urbanist font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-400 rounded-lg hover:from-orange-600 hover:to-orange-500 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 active:scale-[0.98]"
              disabled={loading}
            >
              {loading ? "Saving..." : "Next"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Form3;
